import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { 
  ChevronDown,
  HelpCircle,
  CreditCard,
  Plane,
  Car,
  RefreshCw,
  MapPin,
  Shield,
  HeadphonesIcon,
  Lock,
  CheckCircle2,
  MessageCircle,
  Gift
} from 'lucide-react';
import { Button } from '../components/ui/button';

// FAQ Data organized by category
const faqData = {
  freeServices: {
    title: "Free Services & Benefits",
    icon: Gift,
    questions: [
      {
        q: "Is Meet & Greet included in my transfer?",
        a: "Yes! Complimentary Meet & Greet service is included FREE with every GoTurkeyTransfer booking. This is a €15 value that we provide at no extra cost. Your professional driver will meet you at the airport arrivals hall holding a personalized name board with your name, making it easy to find them after collecting your luggage."
      },
      {
        q: "Do you offer free cancellation?",
        a: "Yes, we offer completely free cancellation on all bookings. You can cancel your transfer up to 24 hours before your scheduled pickup time and receive a full refund — no questions asked, no cancellation fees. This gives you the flexibility to change your plans without any financial risk."
      },
      {
        q: "Can I cancel my booking for free?",
        a: "Absolutely! Free cancellation is available on all GoTurkeyTransfer bookings. Simply cancel at least 24 hours before your scheduled pickup, and you'll receive a 100% refund. Cancellations can be made through the link in your confirmation email or by contacting our 24/7 support team."
      },
      {
        q: "What services are included in the transfer price?",
        a: "Your transfer price includes: Free Meet & Greet service at arrivals (€15 value), real-time flight monitoring, up to 60 minutes free waiting time at airports, all tolls and fuel charges, door-to-door private service, and 24/7 customer support. There are absolutely no hidden fees."
      },
      {
        q: "Is flight monitoring included?",
        a: "Yes, flight monitoring is included at no extra charge. We track your flight in real-time, so if your flight is delayed or arrives early, your driver will automatically adjust their arrival time. You don't need to contact us — we'll be there when you land."
      }
    ]
  },
  booking: {
    title: "Booking & Reservations",
    icon: HelpCircle,
    questions: [
      {
        q: "How do I book a VIP airport transfer?",
        a: "Booking with GoTurkeyTransfer is simple and takes just a few minutes. Enter your pickup airport or location, destination address, travel date and time, number of passengers, and contact details. Choose your preferred vehicle class, review the fixed price, and complete your secure payment. You'll receive an instant confirmation email with all your transfer details."
      },
      {
        q: "Can I book last-minute or same-day transfers?",
        a: "Yes, we accept same-day bookings subject to vehicle availability. For the best selection and guaranteed service, we recommend booking at least 24 hours in advance. For last-minute requests, please book online and our team will confirm availability within minutes."
      },
      {
        q: "What information is required to complete a booking?",
        a: "To complete your booking, we need: your flight number (for airport pickups), pickup location and destination address, date and time of transfer, number of passengers and luggage pieces, passenger name and contact number, and email address for confirmation. This ensures a smooth pickup experience."
      },
      {
        q: "Will I receive a booking confirmation email?",
        a: "Yes, immediately after completing your booking, you'll receive a detailed confirmation email containing your booking reference number, driver meeting instructions, vehicle details, pickup time, and our 24/7 support contact information. Please check your spam folder if you don't see it within a few minutes."
      },
      {
        q: "Can I book a transfer for someone else?",
        a: "Absolutely. Many customers book transfers for family members, colleagues, or guests. Simply enter the passenger's details and contact information during booking. The confirmation will be sent to your email, and you can forward the details to the traveler."
      }
    ]
  },
  payment: {
    title: "Payment & Pricing",
    icon: CreditCard,
    questions: [
      {
        q: "How are prices calculated?",
        a: "Our prices are calculated based on the distance between pickup and drop-off locations, vehicle type selected, and time of service. The price you see during booking is the final price — no surge pricing, no hidden fees. All prices include Free Meet & Greet service (€15 value), flight monitoring, waiting time, and tolls."
      },
      {
        q: "Are prices fixed or can they change?",
        a: "All GoTurkeyTransfer prices are fixed and guaranteed at the time of booking. Unlike taxis or ride-hailing apps, our prices never change due to traffic, delays, or demand. The price you confirm is exactly what you pay — no surprises."
      },
      {
        q: "What payment methods do you accept?",
        a: "We accept all major credit and debit cards (Visa, Mastercard, American Express), PayPal, and selected local payment methods. All payments are processed through secure, encrypted payment gateways. You can also choose to pay cash to the driver in local currency (Turkish Lira) or Euros."
      },
      {
        q: "Is my payment secure?",
        a: "Yes, absolutely. All online payments are processed through PCI-DSS compliant payment providers with bank-level encryption. We never store your full card details on our servers. Your financial information is protected by industry-leading security standards."
      },
      {
        q: "Do you charge any hidden fees?",
        a: "No, never. GoTurkeyTransfer operates on a transparent pricing model. The price shown during booking includes all fees — vehicle, driver, fuel, tolls, meet & greet, flight monitoring, and standard waiting time. There are no hidden charges, airport fees, or surprise costs added later."
      }
    ]
  },
  pickup: {
    title: "Airport Pickup & Service",
    icon: Plane,
    questions: [
      {
        q: "Where will my driver meet me at the airport?",
        a: "Your driver will meet you in the arrivals hall, just after you exit customs and baggage claim. They will hold a sign with your name clearly displayed. Exact meeting point details are included in your booking confirmation email and SMS."
      },
      {
        q: "What happens if my flight is delayed?",
        a: "Don't worry — we monitor all flights in real-time. If your flight is delayed, your driver will automatically adjust their arrival time accordingly. There's no need to contact us or pay extra. Your transfer is guaranteed regardless of delays."
      },
      {
        q: "How will I recognize my driver?",
        a: "Your driver will be waiting in the arrivals area holding a name board with your name. They will be professionally dressed. You'll also receive your driver's name and contact number via SMS or WhatsApp shortly before your flight lands, so you can reach them directly if needed."
      },
      {
        q: "Do you monitor flight arrival times?",
        a: "Yes, we actively track all flight arrivals using live flight data. Our system automatically updates your pickup time if your flight arrives early or late. This means your driver will always be there when you land — not before, not hours after."
      },
      {
        q: "How long will my driver wait at the airport?",
        a: "For airport pickups, your driver will wait up to 60 minutes after your flight lands at no extra charge. This gives you plenty of time to clear immigration, collect luggage, and reach the meeting point. If you need more time, simply contact your driver directly."
      }
    ]
  },
  vehicles: {
    title: "Vehicles & Comfort",
    icon: Car,
    questions: [
      {
        q: "What type of vehicles do you use?",
        a: "GoTurkeyTransfer partners with providers who operate premium vehicles only. Our fleet includes Mercedes E-Class, Mercedes Vito, Mercedes Sprinter, and similar luxury vehicles. All vehicles are modern (less than 5 years old), fully licensed, insured, air-conditioned, and regularly maintained."
      },
      {
        q: "How many passengers and luggage can I bring?",
        a: "Vehicle capacity varies by type: Sedan (Mercedes E-Class) fits 3 passengers with 3 suitcases. Minivan (Mercedes Vito) fits 6 passengers with 6 suitcases. Minibus (Mercedes Sprinter) fits up to 12 passengers with luggage. Choose the appropriate vehicle during booking based on your group size."
      },
      {
        q: "Are child seats available?",
        a: "Yes, child seats and booster seats are available upon request at no additional charge. Please specify the number and type of child seats needed (infant, toddler, or booster) when making your booking, and we'll ensure they're installed and ready."
      },
      {
        q: "Is the service private or shared?",
        a: "All GoTurkeyTransfer transfers are 100% private. You will never share your vehicle with other passengers or make stops for other pickups. Your transfer is direct from pickup to destination, ensuring privacy, comfort, and the fastest possible journey."
      },
      {
        q: "Are vehicles equipped with Wi-Fi and charging ports?",
        a: "Most premium vehicles in our network are equipped with complimentary Wi-Fi and USB charging ports. If connectivity during your transfer is essential, please mention this in the booking notes and we'll confirm availability for your specific vehicle."
      }
    ]
  },
  cancellation: {
    title: "Cancellations & Changes",
    icon: RefreshCw,
    questions: [
      {
        q: "Can I cancel my booking?",
        a: "Yes, you can cancel your booking at any time. Cancellations made more than 24 hours before the scheduled pickup receive a full refund. Cancellations made within 24 hours may be subject to a cancellation fee. No-shows are non-refundable."
      },
      {
        q: "How do I modify or cancel my reservation?",
        a: "You can modify or cancel your booking through the link in your confirmation email, or by contacting our 24/7 customer support team via email or live chat. Changes to pickup time, location, or passenger details can usually be accommodated at no extra charge if requested in advance."
      },
      {
        q: "Will I receive a refund for cancellations?",
        a: "Refunds are processed according to our cancellation policy: Full refund for cancellations 24+ hours before pickup. 50% refund for cancellations 12-24 hours before pickup. No refund for cancellations less than 12 hours before pickup or no-shows. Refunds are processed within 5-10 business days."
      },
      {
        q: "How long does the refund process take?",
        a: "Once a cancellation is confirmed, refunds are typically processed within 5-10 business days, depending on your payment method and bank. Credit card refunds may take 5-7 days to appear on your statement. PayPal refunds are usually faster, within 3-5 days."
      },
      {
        q: "What if I need to change my flight details?",
        a: "You can update your flight details at any time before your pickup — just contact our support team or use the modification link in your confirmation email. We recommend updating flight information as soon as possible so we can adjust monitoring and driver scheduling accordingly."
      }
    ]
  },
  coverage: {
    title: "Coverage & Destinations",
    icon: MapPin,
    questions: [
      {
        q: "Which airports in Turkey do you serve?",
        a: "GoTurkeyTransfer operates from all major Turkish airports including: Istanbul Airport (IST), Sabiha Gökçen Airport (SAW), Antalya Airport (AYT), Bodrum Milas Airport (BJV), Dalaman Airport (DLM), Izmir Adnan Menderes Airport (ADB), Ankara Esenboğa Airport (ESB), and more. If your airport isn't listed, contact us — we likely cover it."
      },
      {
        q: "Do you provide transfers to hotels, villas, or private addresses?",
        a: "Yes, we provide door-to-door transfers to any destination including hotels, resorts, villas, private residences, cruise ports, and business addresses. Simply enter your exact destination address during booking, and your driver will take you directly there."
      },
      {
        q: "Is the service available 24/7?",
        a: "Yes, GoTurkeyTransfer operates 24 hours a day, 7 days a week, 365 days a year. Whether your flight arrives at 3 AM or departs at midnight, we're available. Late-night and early-morning transfers are priced the same as daytime transfers — no surcharges."
      },
      {
        q: "Can I book round-trip transfers?",
        a: "Absolutely. You can book both your airport arrival transfer and departure transfer at the same time. Round-trip bookings are managed as two separate transfers, giving you flexibility if your plans change. Many customers save time by booking both legs together."
      },
      {
        q: "Do you offer inter-city transfers?",
        a: "Yes, we provide long-distance transfers between cities in Turkey. Popular routes include Istanbul to Bursa, Antalya to Cappadocia, and more. Contact us for a custom quote on inter-city journeys. These transfers include rest stops as needed for passenger comfort."
      }
    ]
  }
};

// Generate JSON-LD FAQ Schema
const generateFaqSchema = () => {
  const allQuestions = [];
  Object.values(faqData).forEach(category => {
    category.questions.forEach(item => {
      allQuestions.push({
        "@type": "Question",
        "name": item.q,
        "acceptedAnswer": {
          "@type": "Answer",
          "text": item.a
        }
      });
    });
  });
  
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": allQuestions
  };
};

// Accordion Item Component
const FaqItem = ({ question, answer, isOpen, onClick }) => {
  return (
    <div className="border border-white/10 rounded-xl overflow-hidden bg-secondary hover:bg-secondary/80 transition-colors">
      <button
        onClick={onClick}
        className="w-full flex items-center justify-between p-5 text-left"
        aria-expanded={isOpen}
      >
        <span className="font-medium text-white pr-4">{question}</span>
        <ChevronDown 
          className={`h-5 w-5 text-orange-500 flex-shrink-0 transition-transform duration-300 ${isOpen ? 'rotate-180' : ''}`}
        />
      </button>
      <div 
        className={`overflow-hidden transition-all duration-300 ${isOpen ? 'max-h-96' : 'max-h-0'}`}
      >
        <div className="px-5 pb-5 text-gray-300 leading-relaxed">
          {answer}
        </div>
      </div>
    </div>
  );
};

// FAQ Category Component
const FaqCategory = ({ title, icon: Icon, questions, openIndex, setOpenIndex, categoryKey }) => {
  return (
    <div className="mb-12">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-10 h-10 bg-orange-500/10 rounded-xl flex items-center justify-center">
          <Icon className="h-5 w-5 text-orange-500" />
        </div>
        <h2 className="text-2xl font-bold text-white">{title}</h2>
      </div>
      <div className="space-y-3">
        {questions.map((item, index) => {
          const uniqueKey = `${categoryKey}-${index}`;
          return (
            <FaqItem
              key={uniqueKey}
              question={item.q}
              answer={item.a}
              isOpen={openIndex === uniqueKey}
              onClick={() => setOpenIndex(openIndex === uniqueKey ? null : uniqueKey)}
            />
          );
        })}
      </div>
    </div>
  );
};

const FaqPage = () => {
  const [openIndex, setOpenIndex] = useState(null);
  const navigate = useNavigate();

  // Build FAQ data for schema
  const allFaqItems = [];
  Object.values(faqData).forEach(category => {
    category.questions.forEach(item => {
      allFaqItems.push({ question: item.q, answer: item.a });
    });
  });

  // Handle navigation to home with scroll to top
  const handleBookNow = () => {
    window.scrollTo(0, 0);
    navigate('/');
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />

      <main className="pt-20">
        {/* Hero Section */}
        <section className="relative bg-gradient-to-br from-background via-background to-orange-500/5 py-16 lg:py-24">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto text-center">
              <div className="inline-flex items-center gap-2 bg-orange-500/10 border border-orange-500/20 rounded-full px-4 py-2 mb-6">
                <MessageCircle className="h-4 w-4 text-orange-500" />
                <span className="text-sm font-medium text-orange-500">We're Here to Help</span>
              </div>
              
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-foreground mb-6 leading-tight">
                Frequently Asked <span className="text-orange-500">Questions</span>
              </h1>
              
              <p className="text-xl text-muted-foreground mb-8">
                Everything you need to know about VIP airport transfers in Turkey
              </p>
              
              <p className="text-lg text-muted-foreground max-w-3xl mx-auto leading-relaxed">
                GoTurkeyTransfer provides reliable, premium airport transfer services across Turkey. 
                Our licensed service providers, transparent pricing, and 24/7 customer support 
                ensure a stress-free travel experience. Browse our FAQ below or contact us directly 
                for personalized assistance.
              </p>

              {/* Free Cancellation Highlight */}
              <div className="mt-10 max-w-xl mx-auto bg-gradient-to-r from-green-500/10 to-emerald-500/10 border border-green-500/30 rounded-xl p-5">
                <div className="flex items-center justify-center gap-3">
                  <RefreshCw className="h-6 w-6 text-green-400" />
                  <div className="text-left">
                    <p className="text-lg font-bold text-green-400">Free Cancellation Policy</p>
                    <p className="text-sm text-green-300/80">Cancel free up to 24 hours before your pickup - no questions asked</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Quick Navigation */}
        <section className="py-8 border-b border-border bg-card/30">
          <div className="container mx-auto px-4">
            <div className="flex flex-wrap justify-center gap-3">
              {Object.entries(faqData).map(([key, category]) => {
                const Icon = category.icon;
                return (
                  <a
                    key={key}
                    href={`#${key}`}
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-background border border-border hover:border-orange-500/50 hover:bg-orange-500/5 transition-all text-sm text-muted-foreground hover:text-foreground"
                  >
                    <Icon className="h-4 w-4" />
                    {category.title}
                  </a>
                );
              })}
            </div>
          </div>
        </section>

        {/* FAQ Categories */}
        <section className="py-16 lg:py-24">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              {Object.entries(faqData).map(([key, category]) => (
                <div key={key} id={key}>
                  <FaqCategory
                    title={category.title}
                    icon={category.icon}
                    questions={category.questions}
                    openIndex={openIndex}
                    setOpenIndex={setOpenIndex}
                    categoryKey={key}
                  />
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Trust & Support Section */}
        <section className="py-16 lg:py-24 bg-secondary">
          <div className="container mx-auto px-4">
            <div className="max-w-6xl mx-auto">
              <div className="text-center mb-12">
                <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
                  Your Trust is Our Priority
                </h2>
                <p className="text-lg text-gray-300 max-w-2xl mx-auto">
                  GoTurkeyTransfer is committed to providing safe, reliable, and transparent airport transfer services.
                </p>
              </div>
              
              <div className="grid md:grid-cols-3 gap-6">
                <div className="bg-black/50 border border-white/10 rounded-2xl p-6 text-center">
                  <div className="w-14 h-14 bg-orange-500/10 rounded-xl flex items-center justify-center mx-auto mb-4">
                    <Shield className="h-7 w-7 text-orange-500" />
                  </div>
                  <h3 className="font-semibold text-white mb-2">Licensed Providers</h3>
                  <p className="text-sm text-gray-400">
                    All transfers are operated by vetted, licensed third-party service providers 
                    who meet our strict quality and safety standards.
                  </p>
                </div>
                
                <div className="bg-black/50 border border-white/10 rounded-2xl p-6 text-center">
                  <div className="w-14 h-14 bg-orange-500/10 rounded-xl flex items-center justify-center mx-auto mb-4">
                    <Lock className="h-7 w-7 text-orange-500" />
                  </div>
                  <h3 className="font-semibold text-white mb-2">GDPR Compliant</h3>
                  <p className="text-sm text-gray-400">
                    Your personal data is handled according to GDPR standards. We only share 
                    necessary information with service providers to fulfill your booking.
                  </p>
                </div>
                
                <div className="bg-black/50 border border-white/10 rounded-2xl p-6 text-center">
                  <div className="w-14 h-14 bg-orange-500/10 rounded-xl flex items-center justify-center mx-auto mb-4">
                    <HeadphonesIcon className="h-7 w-7 text-orange-500" />
                  </div>
                  <h3 className="font-semibold text-white mb-2">24/7 Support</h3>
                  <p className="text-sm text-gray-400">
                    Our customer support team is available around the clock to assist with 
                    bookings, changes, or any questions during your journey.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Still Have Questions */}
        <section className="py-16">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <div className="bg-gradient-to-br from-secondary to-secondary/80 border border-white/10 rounded-2xl p-8 md:p-12">
                <div className="grid md:grid-cols-2 gap-8 items-center">
                  <div>
                    <h2 className="text-2xl sm:text-3xl font-bold text-white mb-4">
                      Still Have Questions?
                    </h2>
                    <p className="text-gray-300 mb-6">
                      Can't find what you're looking for? Our friendly support team is ready 
                      to help you with any questions about your airport transfer.
                    </p>
                    <div className="flex flex-col sm:flex-row gap-4">
                      <Link to="/contact/">
                        <Button variant="outline" className="gap-2 border-white/20 text-white hover:bg-white/10">
                          <MessageCircle className="h-4 w-4" />
                          Contact Support
                        </Button>
                      </Link>
                      <a href="mailto:support@goturkeytransfer.com">
                        <Button variant="ghost" className="gap-2 text-gray-300 hover:text-white">
                          support@goturkeytransfer.com
                        </Button>
                      </a>
                    </div>
                  </div>
                  <div className="hidden md:flex justify-center">
                    <div className="relative">
                      <div className="w-32 h-32 bg-orange-500/10 rounded-full flex items-center justify-center">
                        <HeadphonesIcon className="h-16 w-16 text-orange-500" />
                      </div>
                      <div className="absolute -top-2 -right-2 w-8 h-8 bg-green-500 rounded-full flex items-center justify-center">
                        <CheckCircle2 className="h-5 w-5 text-white" />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20 lg:py-28 bg-gradient-to-br from-orange-500 to-orange-600 relative overflow-hidden">
          <div className="absolute top-0 left-0 w-96 h-96 bg-white/10 rounded-full blur-3xl -translate-x-1/2 -translate-y-1/2"></div>
          <div className="absolute bottom-0 right-0 w-96 h-96 bg-black/10 rounded-full blur-3xl translate-x-1/2 translate-y-1/2"></div>
          
          <div className="container mx-auto px-4 relative">
            <div className="max-w-3xl mx-auto text-center">
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-6">
                Ready to Travel in Comfort?
              </h2>
              <p className="text-xl text-white/90 mb-8">
                Book your premium VIP airport transfer today and start your Turkish journey 
                with confidence and style.
              </p>
              <Button 
                  size="lg" 
                  className="bg-white text-orange-500 hover:bg-white/90 text-lg px-8 py-6 h-auto font-semibold shadow-lg"
                  data-testid="book-transfer-faq-cta"
                  onClick={handleBookNow}
                >
                  Book Your VIP Transfer
                </Button>
              <p className="mt-6 text-white/70 text-sm">
                Instant confirmation • Fixed prices • Free cancellation up to 24h
              </p>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default FaqPage;
